const BASE = "/api";

let sessionExpiredHandler = null;
// Registered by App.jsx on mount. Fires whenever any API call (other than
// login/me, which have their own normal handling) comes back 401 — meaning
// the session cookie is missing or expired mid-use, not a login failure.
export function setSessionExpiredHandler(fn) {
  sessionExpiredHandler = fn;
}

async function request(path, options = {}) {
  const res = await fetch(BASE + path, {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    ...options,
  });

  if (res.status === 401 && path !== "/auth/login" && path !== "/auth/me" && sessionExpiredHandler) {
    sessionExpiredHandler();
  }

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.error || "Request failed");
    err.status = res.status;
    err.data = data; // e.g. the list of missing items on a 422
    throw err;
  }
  return data;
}


export const api = {
  login: (email, password) =>
    request("/auth/login", { method: "POST", body: JSON.stringify({ email, password }) }),
  logout: () => request("/auth/logout", { method: "POST" }),
  me: () => request("/auth/me"),

  users: {
    list: () => request("/auth/users"),
    create: (data) => request("/auth/users", { method: "POST", body: JSON.stringify(data) }),
    remove: (id) => request(`/auth/users/${id}`, { method: "DELETE" }),
    resetPassword: (id, password) =>
      request(`/auth/users/${id}/reset-password`, { method: "POST", body: JSON.stringify({ password }) }),
  },
  jybek: {
    ask: (message) => request("/jybek/ask", { method: "POST", body: JSON.stringify({ message }) }),
  },

  courses: {
    list: () => request("/courses"),
    get: (id) => request(`/courses/${id}`),
    create: (data) => request("/courses", { method: "POST", body: JSON.stringify(data) }),
    update: (id, data) => request(`/courses/${id}`, { method: "PUT", body: JSON.stringify(data) }),
    remove: (id) => request(`/courses/${id}`, { method: "DELETE" }),
    addLesson: (courseId, data) =>
      request(`/courses/${courseId}/lessons`, { method: "POST", body: JSON.stringify(data) }),
    updateLesson: (lessonId, data) =>
      request(`/courses/lessons/${lessonId}`, { method: "PUT", body: JSON.stringify(data) }),
    removeLesson: (lessonId) => request(`/courses/lessons/${lessonId}`, { method: "DELETE" }),
  },

  progress: {
    completeLesson: (lessonId) => request(`/progress/lessons/${lessonId}/complete`, { method: "POST" }),
    submitQuiz: (lessonId, answers) =>
      request(`/progress/lessons/${lessonId}/quiz`, { method: "POST", body: JSON.stringify({ answers }) }),
  },

  dashboard: () => request("/dashboard"),

  hr: {
    list: (resource) => request(`/hr/${resource}`),
    create: (resource, data) => request(`/hr/${resource}`, { method: "POST", body: JSON.stringify(data) }),
    update: (resource, id, data) => request(`/hr/${resource}/${id}`, { method: "PUT", body: JSON.stringify(data) }),
    remove: (resource, id) => request(`/hr/${resource}/${id}`, { method: "DELETE" }),
  },

  reports: {
    profitLoss: (from, to) => request(`/reports/profit-loss?from=${from}&to=${to}`),
    profitLossExportUrl: (from, to) => `${BASE}/reports/profit-loss/export.csv?from=${from}&to=${to}`,
  },

  employees: {
    list: () => request("/employees"),
    get: (employeeId) => request(`/employees/${employeeId}`),
    create: (data) => request("/employees", { method: "POST", body: JSON.stringify(data) }),
    update: (employeeId, data) => request(`/employees/${employeeId}`, { method: "PUT", body: JSON.stringify(data) }),
    revealSsn: (employeeId) => request(`/employees/${employeeId}/ssn/reveal`, { method: "POST" }),
    compliance: {
      get: (employeeId) => request(`/employees/${employeeId}/compliance`),
      sendReminder: (employeeId, data) => request(`/employees/${employeeId}/compliance/reminder`, { method: "POST", body: JSON.stringify(data) }),
      readiness: () => request(`/employees/compliance/readiness`),
      runReminderScan: (force = false) => request(`/employees/compliance/run-reminder-scan`, { method: "POST", body: JSON.stringify({ force }) }),
    },
    terminate: (employeeId, data) => request(`/employees/${employeeId}/terminate`, { method: "POST", body: JSON.stringify(data) }),
    reinstate: (employeeId) => request(`/employees/${employeeId}/reinstate`, { method: "POST" }),
    exitInterviews: {
      add: (employeeId, data) => request(`/employees/${employeeId}/exit-interviews`, { method: "POST", body: JSON.stringify(data) }),
      update: (id, data) => request(`/employees/exit-interviews/${id}`, { method: "PUT", body: JSON.stringify(data) }),
      remove: (id) => request(`/employees/exit-interviews/${id}`, { method: "DELETE" }),
    },
    onboarding: {
      seed: (employeeId) => request(`/employees/${employeeId}/onboarding/seed`, { method: "POST" }),
      reconcile: (employeeId) => request(`/employees/${employeeId}/onboarding/reconcile`, { method: "POST" }),
      summary: (employeeId) => request(`/employees/${employeeId}/onboarding/summary`),
      update: (id, data) => request(`/employees/onboarding-tasks/${id}`, { method: "PUT", body: JSON.stringify(data) }),
    },
    photoUrl: (employeeId) => `${BASE}/employees/${employeeId}/photo`,
    uploadPhoto: (employeeId, imageDataUrl) =>
      request(`/employees/${employeeId}/photo`, { method: "POST", body: JSON.stringify({ image: imageDataUrl }) }),
    approvePhoto: (employeeId) => request(`/employees/${employeeId}/photo/approve`, { method: "POST" }),
    removePhoto: (employeeId) => request(`/employees/${employeeId}/photo`, { method: "DELETE" }),
    qrCode: (employeeId) => request(`/employees/${employeeId}/qr-code`),
    timeline: {
      list: (employeeId) => request(`/employees/${employeeId}/timeline`),
      add: (employeeId, data) => request(`/employees/${employeeId}/timeline`, { method: "POST", body: JSON.stringify(data) }),
    },
  },

  caregivers: {
    list: () => request("/caregivers"),
    get: (employeeId) => request(`/caregivers/${employeeId}`),
    create: (data) => request("/caregivers", { method: "POST", body: JSON.stringify(data) }),
    update: (employeeId, data) => request(`/caregivers/${employeeId}`, { method: "PUT", body: JSON.stringify(data) }),
    skills: {
      add: (employeeId, data) => request(`/caregivers/${employeeId}/skills`, { method: "POST", body: JSON.stringify(data) }),
      update: (id, data) => request(`/caregivers/skills/${id}`, { method: "PUT", body: JSON.stringify(data) }),
      remove: (id) => request(`/caregivers/skills/${id}`, { method: "DELETE" }),
    },
    documents: {
      add: (employeeId, data) => request(`/caregivers/${employeeId}/documents`, { method: "POST", body: JSON.stringify(data) }),
      upload: (employeeId, data) => request(`/caregivers/${employeeId}/documents/upload`, { method: "POST", body: JSON.stringify(data) }),
      update: (id, data) => request(`/caregivers/documents/${id}`, { method: "PUT", body: JSON.stringify(data) }),
      remove: (id) => request(`/caregivers/documents/${id}`, { method: "DELETE" }),
      fileUrl: (id, download = false) => `${BASE}/caregivers/documents/${id}/file${download ? "?download=1" : ""}`,
    },
    assignments: {
      add: (employeeId, data) => request(`/caregivers/${employeeId}/assignments`, { method: "POST", body: JSON.stringify(data) }),
      update: (id, data) => request(`/caregivers/assignments/${id}`, { method: "PUT", body: JSON.stringify(data) }),
      remove: (id) => request(`/caregivers/assignments/${id}`, { method: "DELETE" }),
    },
  },

  recruitment: {
    // Public — no cookie/session involved, used by the unauthenticated /apply page.
    apply: (data) => request("/recruitment/apply", { method: "POST", body: JSON.stringify(data) }),
    jobTypes: () => request("/recruitment/job-types"),

    // Admin-only — used by Recruitment.jsx / ApplicantProfile.jsx.
    list: () => request("/recruitment"),
    get: (id) => request(`/recruitment/${id}`),
    approve: (id) => request(`/recruitment/${id}/approve`, { method: "POST" }),
    reject: (id, reason) =>
      request(`/recruitment/${id}/reject`, { method: "POST", body: JSON.stringify({ reason }) }),
    updateReference: (id, data) =>
      request(`/recruitment/references/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  },

  portal: {
    dashboard: () => request("/portal/dashboard"),
    shifts: () => request("/portal/shifts"),
    respondToShift: (id, action, notes = "") => request(`/portal/shifts/${id}/respond`, { method: "POST", body: JSON.stringify({ action, notes }) }),
    compliance: () => request("/portal/compliance"),
  },

  clients: {
    list: (q = "") => request(`/clients${q ? `?q=${encodeURIComponent(q)}` : ""}`),
    get: (clientId) => request(`/clients/${clientId}`),
    create: (data) => request('/clients', { method: 'POST', body: JSON.stringify(data) }),
    update: (clientId, data) => request(`/clients/${clientId}`, { method: 'PUT', body: JSON.stringify(data) }),
    updateAdmissionItem: (clientId, itemId, data) => request(`/clients/${clientId}/admission-items/${itemId}`, { method: 'PUT', body: JSON.stringify(data) }),
  },

  // Client / family e-signature requests (server/routes/client-esign.js)
  clientEsign: {
    list: (cid) => request(`/clients/${cid}/esign`),
    create: (cid, body) => request(`/clients/${cid}/esign`, { method: "POST", body: JSON.stringify(body) }),
    get: (cid, rid) => request(`/clients/${cid}/esign/${rid}`),
    doc: (cid, rid, key) => request(`/clients/${cid}/esign/${rid}/docs/${key}`),
    save: (cid, rid, key, values) => request(`/clients/${cid}/esign/${rid}/docs/${key}`, { method: "PUT", body: JSON.stringify(values) }),
    stamp: (cid, rid, key, stampKey) => request(`/clients/${cid}/esign/${rid}/docs/${key}/stamp`, { method: "POST", body: JSON.stringify({ key: stampKey }) }),
    send: (cid, rid) => request(`/clients/${cid}/esign/${rid}/send`, { method: "POST" }),
    cancel: (cid, rid) => request(`/clients/${cid}/esign/${rid}/cancel`, { method: "POST" }),
    approve: (cid, rid) => request(`/clients/${cid}/esign/${rid}/approve`, { method: "POST" }),
    previewUrl: (cid, rid, key) => `${BASE}/clients/${cid}/esign/${rid}/docs/${key}/preview.pdf`,
    mySignature: (cid) => request(`/clients/${cid}/esign/my-signature`),
    adoptMySignature: (cid, body) => request(`/clients/${cid}/esign/my-signature`, { method: "POST", body: JSON.stringify(body) }),
  },

  executive: {
    overview: () => request("/executive/overview"),
  },

  // Office staff accounts with per-page permissions (server/routes/access.js)
  access: {
    me: () => request("/access/me"),
    registry: () => request("/access/registry"),
    users: () => request("/access/users"),
    create: (body) => request("/access/users", { method: "POST", body: JSON.stringify(body) }),
    setPermissions: (id, permissions) => request(`/access/users/${id}/permissions`, { method: "PUT", body: JSON.stringify({ permissions }) }),
    setStatus: (id, status) => request(`/access/users/${id}/status`, { method: "POST", body: JSON.stringify({ status }) }),
    resetPassword: (id) => request(`/access/users/${id}/reset-password`, { method: "POST" }),
    caregiverOptions: () => request("/access/caregiver-options"),
    setCaregiver: (id, body) => request(`/access/users/${id}/caregiver`, { method: "PUT", body: JSON.stringify(body) }),
    fromCaregiver: (body) => request("/access/users/from-caregiver", { method: "POST", body: JSON.stringify(body) }),
  },

  // Read-only inspector (surveyor) portal (server/routes/inspector.js)
  inspector: {
    me: () => request("/inspector/view/me"),
    overview: () => request("/inspector/view/overview"),
    personnel: () => request("/inspector/view/personnel"),
    personnelFile: (id) => request(`/inspector/view/personnel/${encodeURIComponent(id)}`),
    documentUrl: (id) => `${BASE}/inspector/view/documents/${id}`,
    clients: () => request("/inspector/view/clients"),
    client: (id) => request(`/inspector/view/clients/${encodeURIComponent(id)}`),
    clientDocumentUrl: (clientId, docId) => `${BASE}/inspector/view/clients/${encodeURIComponent(clientId)}/documents/${docId}`,
    programs: () => request("/inspector/view/programs"),
    manuals: () => request("/inspector/view/manuals"),
    manualUrl: (id) => `${BASE}/inspector/view/manuals/${id}`,
    admin: {
      accounts: () => request("/inspector/admin/accounts"),
      create: (body) => request("/inspector/admin/accounts", { method: "POST", body: JSON.stringify(body) }),
      extend: (userId, days) => request(`/inspector/admin/accounts/${userId}/extend`, { method: "POST", body: JSON.stringify({ days }) }),
      revoke: (userId) => request(`/inspector/admin/accounts/${userId}/revoke`, { method: "POST" }),
      resetPassword: (userId) => request(`/inspector/admin/accounts/${userId}/reset-password`, { method: "POST" }),
      log: (userId) => request(`/inspector/admin/log${userId ? `?user_id=${userId}` : ""}`),
      manuals: () => request("/inspector/admin/manuals"),
      uploadManual: (body) => request("/inspector/admin/manuals", { method: "POST", body: JSON.stringify(body) }),
      updateManual: (id, body) => request(`/inspector/admin/manuals/${id}`, { method: "PUT", body: JSON.stringify(body) }),
      manualFileUrl: (id) => `${BASE}/inspector/admin/manuals/${id}/file`,
    },
  },

  // Digital New Hire Packet + e-signature (server/routes/onboarding-packet.js)
  onboarding: {
    handbookUrl: `${BASE}/onboarding-packet/handbook.pdf`,
    me: () => request("/onboarding-packet/me"),
    consent: (body) => request("/onboarding-packet/me/consent", { method: "POST", body: JSON.stringify(body) }),
    adopt: (body) => request("/onboarding-packet/me/adopt", { method: "POST", body: JSON.stringify(body) }),
    form: (fid) => request(`/onboarding-packet/me/forms/${fid}`),
    save: (fid, values) => request(`/onboarding-packet/me/forms/${fid}`, { method: "PUT", body: JSON.stringify(values) }),
    stamp: (fid, key) => request(`/onboarding-packet/me/forms/${fid}/stamp`, { method: "POST", body: JSON.stringify({ key }) }),
    submit: (fid) => request(`/onboarding-packet/me/forms/${fid}/submit`, { method: "POST" }),
    upload: (fid, data) => request(`/onboarding-packet/me/forms/${fid}/upload`, { method: "POST", body: JSON.stringify(data) }),
    fileUrl: (id) => `${BASE}/onboarding-packet/me/files/${id}`,
    documentUrl: (id) => `${BASE}/onboarding-packet/me/documents/${id}`,
    hr: {
      signer: () => request("/onboarding-packet/hr/signer"),
      adoptSigner: (body) => request("/onboarding-packet/hr/signer", { method: "POST", body: JSON.stringify(body) }),
      list: () => request("/onboarding-packet/hr/packets"),
      byEmployee: (employeeId) => request(`/onboarding-packet/hr/by-employee/${encodeURIComponent(employeeId)}`),
      create: (body) => request("/onboarding-packet/hr/packets", { method: "POST", body: JSON.stringify(body) }),
      get: (id) => request(`/onboarding-packet/hr/packets/${id}`),
      form: (id, fid) => request(`/onboarding-packet/hr/packets/${id}/forms/${fid}`),
      save: (id, fid, values) => request(`/onboarding-packet/hr/packets/${id}/forms/${fid}`, { method: "PUT", body: JSON.stringify(values) }),
      stamp: (id, fid, key) => request(`/onboarding-packet/hr/packets/${id}/forms/${fid}/stamp`, { method: "POST", body: JSON.stringify({ key }) }),
      reopen: (id, fid, reason) => request(`/onboarding-packet/hr/packets/${id}/forms/${fid}/reopen`, { method: "POST", body: JSON.stringify({ reason }) }),
      openOrientation: (id) => request(`/onboarding-packet/hr/packets/${id}/stage2`, { method: "POST" }),
      openAnnual: (id, year) => request(`/onboarding-packet/hr/packets/${id}/annual`, { method: "POST", body: JSON.stringify({ year }) }),
      upload: (id, data) => request(`/onboarding-packet/hr/packets/${id}/upload`, { method: "POST", body: JSON.stringify(data) }),
      generatePdf: (id) => request(`/onboarding-packet/hr/packets/${id}/pdf`, { method: "POST" }),
      certify: (id) => request(`/onboarding-packet/hr/packets/${id}/certify`, { method: "POST" }),
      fileUrl: (id, fileId) => `${BASE}/onboarding-packet/hr/packets/${id}/files/${fileId}`,
      documentUrl: (id, docId, download = false) => `${BASE}/onboarding-packet/hr/packets/${id}/documents/${docId}${download ? "?download=1" : ""}`,
    },
  },
};
